import { useState, useEffect } from 'react';
import { logAudit, AUDIT_ACTIONS, AUDIT_ENTITIES, computeChanges } from '../lib/auditLog';
import { Plus, CreditCard as Edit2, Trash2, X, Save, Camera, MapPin, ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal } from './ConfirmModal';
import { useNotify } from './useNotify';
import { PhotoInput } from './PhotoInput';
import { apiClient } from '../lib/apiClient';
import { LocationPickerModal } from './LocationPickerModal';
import { errorMessage } from '../lib/errorUtils';
import { logEvent } from '../lib/telemetry';
import { PHOTO_BUCKETS, deletePhotoByUrl, uploadPhotoFile } from '../lib/photoUpload';
import { MiniMapPreview } from './MiniMapPreview';
import { readGpsLocation } from '../lib/googleMaps';
import type { GPSLocation } from '../lib/googleMaps';

interface DoubleCheck {
  id: string;
  job_site_id: string;
  description: string;
  size: number;
  gps_location: GPSLocation | null;
  photo_url: string | null;
  notes: string | null;
  created_at: string;
}

interface JobSiteDoubleChecksProps {
  jobSiteId: string;
  jobSiteName: string;
  onDataChange?: () => void;
  /** View-only mode: add / edit / delete affordances are removed entirely. */
  readOnly?: boolean;
}

const SIZE_OPTIONS = [
  1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10, 10.5, 11, 11.5, 12,
];

export function JobSiteDoubleChecks({ jobSiteId, jobSiteName, onDataChange, readOnly = false }: JobSiteDoubleChecksProps) {
  const { t } = useTranslation('inspection');
  const { t: tCommon } = useTranslation('common');
  const { notify, NotifyModal } = useNotify();
  const [doubleChecks, setDoubleChecks] = useState<DoubleCheck[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [expandedDoubleCheck, setExpandedDoubleCheck] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    description: '',
    size: '1',
    gps_location: null as GPSLocation | null,
    notes: '',
    photo: null as File | null,
  });
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; description: string } | null>(null);
  const [gpsModalOpen, setGpsModalOpen] = useState(false);
  // Property's saved location — fallback map center for the GPS picker when a
  // device has no location yet and live GPS is blocked/unavailable in the field.
  const [propertyLocation, setPropertyLocation] = useState<GPSLocation | null>(null);

  useEffect(() => {
    loadDoubleChecks();
    loadPropertyLocation();
  }, [jobSiteId]);

  const loadPropertyLocation = async () => {
    try {
      const js = await apiClient.get<{ gps_location: unknown }>(`/api/job-sites/${jobSiteId}`);
      setPropertyLocation(readGpsLocation(js?.gps_location));
    } catch {
      setPropertyLocation(null);
    }
  };

  const loadDoubleChecks = async () => {
    try {
      const all = await apiClient.get<DoubleCheck[]>('/api/double-checks');
      const filtered = all
        .filter((dc) => dc.job_site_id === jobSiteId)
        .sort((a, b) => a.created_at.localeCompare(b.created_at));
      setDoubleChecks(filtered);
    } catch (err) {
      console.error('Error loading double checks:', err);
    } finally {
      setLoading(false);
    }
  };

  const uploadPhoto = async (doubleCheckId: string, file: File): Promise<string | null> => {
    try {
      const fileExt = file.name.split('.').pop();
      const path = `${doubleCheckId}-${Date.now()}.${fileExt}`;
      return await uploadPhotoFile(file, PHOTO_BUCKETS.DOUBLE_CHECK, path);
    } catch (err) {
      console.error('Error uploading photo:', err);
      notify(tCommon('status.errorTitle'), t('dc.errorUploadPhoto'), 'danger');
      return null;
    }
  };

  const deleteDoubleCheckPhoto = async (doubleCheckId: string) => {
    try {
      const oldUrl = doubleChecks.find((d) => d.id === doubleCheckId)?.photo_url ?? null;
      await apiClient.put(`/api/double-checks/${doubleCheckId}`, { photo_url: null });
      await deletePhotoByUrl(oldUrl);
      setPhotoPreview(null);
      setFormData((fd) => ({ ...fd, photo: null }));
      await loadDoubleChecks();
    } catch (err) {
      console.error('Error deleting double check photo:', err);
      notify(tCommon('status.errorTitle'), t('dc.errorDeletePhoto'), 'danger');
    }
  };

  const handleSaveDoubleCheck = async () => {
    if (readOnly) return;
    if (isSaving) return;
    if (!formData.description.trim()) {
      notify(tCommon('status.noticeTitle'), t('dc.errorFillFields'), 'warning');
      return;
    }

    const size = parseFloat(formData.size);
    if (isNaN(size) || size < 1 || size > 12) {
      notify(tCommon('status.noticeTitle'), t('dc.errorInvalidSize'), 'warning');
      return;
    }

    setIsSaving(true);
    try {
      if (editingId) {
        let photoUrl = doubleChecks.find((dc) => dc.id === editingId)?.photo_url;

        if (formData.photo) {
          const uploadedUrl = await uploadPhoto(editingId, formData.photo);
          if (uploadedUrl) photoUrl = uploadedUrl;
        }

        await apiClient.put(`/api/double-checks/${editingId}`, {
          description: formData.description.trim(),
          size,
          gps_location: formData.gps_location,
          notes: formData.notes.trim() || null,
          photo_url: photoUrl,
        });
        void logEvent('inspection_device_updated', `Updated DC ${formData.description.trim()}`, {
          metadata: { device_type: 'double_check', device_id: editingId, label: formData.description.trim(), size, job_site_id: jobSiteId },
        });

        const editedDC = doubleChecks.find((dc) => dc.id === editingId);
        logAudit({
          action: AUDIT_ACTIONS.UPDATE,
          entityType: AUDIT_ENTITIES.DOUBLE_CHECK,
          entityId: editingId,
          entityLabel: formData.description.trim(),
          parentEntityType: AUDIT_ENTITIES.PROPERTY,
          parentEntityId: jobSiteId,
          parentEntityLabel: jobSiteName,
          details: computeChanges(
            { description: editedDC?.description, size: editedDC?.size, notes: editedDC?.notes, photo_url: editedDC?.photo_url },
            { description: formData.description.trim(), size, notes: formData.notes.trim() || null, photo_url: photoUrl },
          ),
          pageContext: 'double_checks',
        });
      } else {
        const newDoubleCheck = await apiClient.post<DoubleCheck>('/api/double-checks', {
          job_site_id: jobSiteId,
          description: formData.description.trim(),
          size,
          gps_location: formData.gps_location,
          notes: formData.notes.trim() || null,
        });

        if (formData.photo && newDoubleCheck) {
          const photoUrl = await uploadPhoto(newDoubleCheck.id, formData.photo);
          if (photoUrl) {
            await apiClient.put(`/api/double-checks/${newDoubleCheck.id}`, { photo_url: photoUrl });
          }
        }
        void logEvent('inspection_device_added', `Added DC ${formData.description.trim()}`, {
          metadata: { device_type: 'double_check', device_id: newDoubleCheck.id, label: formData.description.trim(), size, job_site_id: jobSiteId },
        });
        logAudit({
          action: AUDIT_ACTIONS.CREATE,
          entityType: AUDIT_ENTITIES.DOUBLE_CHECK,
          entityId: newDoubleCheck.id,
          entityLabel: formData.description.trim(),
          parentEntityType: AUDIT_ENTITIES.PROPERTY,
          parentEntityId: jobSiteId,
          parentEntityLabel: jobSiteName,
          details: { description: formData.description.trim(), size },
          pageContext: 'double_checks',
        });
      }

      resetForm();
      await loadDoubleChecks();
      onDataChange?.();
    } catch (err) {
      console.error('Error saving double check:', err);
      notify(tCommon('status.errorTitle'), t('dc.errorSave'), 'danger');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteDoubleCheck = async (id: string, description?: string) => {
    if (readOnly) return;
    try {
      await apiClient.del(`/api/double-checks/${id}`);
      void logEvent('inspection_device_deleted', `Deleted DC ${description || id.slice(0,8)}`, {
        metadata: { device_type: 'double_check', device_id: id, label: description || null, job_site_id: jobSiteId },
      });
      logAudit({
        action: AUDIT_ACTIONS.DELETE,
        entityType: AUDIT_ENTITIES.DOUBLE_CHECK,
        entityId: id,
        entityLabel: description || id,
        parentEntityType: AUDIT_ENTITIES.PROPERTY,
        parentEntityId: jobSiteId,
        parentEntityLabel: jobSiteName,
        pageContext: 'double_checks',
      });
      await loadDoubleChecks();
      onDataChange?.();
    } catch (err) {
      console.error('Error deleting double check:', err);
      notify(tCommon('status.errorTitle'), t('dc.errorDelete', { msg: errorMessage(err) }), 'danger');
    }
  };

  const startEditDoubleCheck = (doubleCheck: DoubleCheck) => {
    setEditingId(doubleCheck.id);
    setFormData({
      description: doubleCheck.description,
      size: doubleCheck.size.toString(),
      gps_location: doubleCheck.gps_location || null,
      notes: doubleCheck.notes || '',
      photo: null,
    });
    setPhotoPreview(doubleCheck.photo_url);
    setIsAdding(false);
  };

  const resetForm = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormData({
      description: '',
      size: '1',
      gps_location: null,
      notes: '',
      photo: null,
    });
    setPhotoPreview(null);
  };

  const toggleDoubleCheck = (doubleCheckId: string) => {
    setExpandedDoubleCheck(expandedDoubleCheck === doubleCheckId ? null : doubleCheckId);
  };

  const handleSetGPS = () => setGpsModalOpen(true);

  const openMap = (location: GPSLocation) => {
    const url = `https://www.google.com/maps?q=${location.latitude},${location.longitude}`;
    window.open(url, '_blank');
  };

  if (loading) {
    return <div className="p-4">{t('dc.loading')}</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-theme-primary">
          {t('dc.headingFor', { name: jobSiteName })}
        </h3>
        {!readOnly && (
          <button
            onClick={() => {
              setEditingId(null);
              setFormData({
                description: '',
                size: '1',
                gps_location: null,
                notes: '',
                photo: null,
              });
              setPhotoPreview(null);
              setIsAdding(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus size={18} />
            {t('dc.add')}
          </button>
        )}
      </div>

      {!readOnly && (isAdding || editingId) && (
        <div className="bg-subtle p-4 rounded-lg border-2 border-blue-200 dark:border-blue-500/30">
          <h4 className="font-semibold text-theme-primary mb-4">
            {editingId ? t('dc.editTitle') : t('dc.newTitle')}
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-theme-primary mb-1">
                {t('dc.descriptionRequired')}
              </label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full p-2 bg-surface text-theme-primary border border-line rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('dc.descriptionPlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-theme-primary mb-1">
                {t('dc.sizeInchesRequired')}
              </label>
              <select
                value={formData.size}
                onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                className="w-full p-2 bg-surface text-theme-primary border border-line rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}"
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-theme-primary mb-2">
                    {t('gps.coordinates')}
                  </label>
                  {formData.gps_location ? (
                    <div className="space-y-2">
                      <div className="text-sm text-theme-secondary">
                        <div className="flex items-center gap-2">
                          <MapPin size={16} className="text-green-600" />
                          <span>{t('gps.lat')} {formData.gps_location.latitude.toFixed(6)}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <MapPin size={16} className="text-green-600" />
                          <span>{t('gps.lng')} {formData.gps_location.longitude.toFixed(6)}</span>
                        </div>
                      </div>
                      <button
                        onClick={handleSetGPS}
                        className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                      >
                        <MapPin size={16} />
                        {t('gps.updateGpsLocation')}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={handleSetGPS}
                      className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                    >
                      <MapPin size={16} />
                      {t('gps.setLocation')}
                    </button>
                  )}
                </div>
                {formData.gps_location && (
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-theme-primary mb-2">
                      {t('gps.locationMap')}
                    </label>
                    <MiniMapPreview
                      latitude={formData.gps_location.latitude}
                      longitude={formData.gps_location.longitude}
                    />
                  </div>
                )}
              </div>
            </div>

            <LocationPickerModal
              open={gpsModalOpen}
              existing={formData.gps_location}
              fallback={propertyLocation}
              onSave={(loc) => setFormData({ ...formData, gps_location: loc })}
              onClose={() => setGpsModalOpen(false)}
            />
            <div className="md:col-span-2">
              <PhotoInput
                label={t('dc.photoLabel')}
                preview={photoPreview}
                onFileChange={(file, preview) => {
                  setFormData((fd) => ({ ...fd, photo: file }));
                  setPhotoPreview(preview);
                }}
                onDelete={editingId
                  ? () => deleteDoubleCheckPhoto(editingId)
                  : () => { setPhotoPreview(null); setFormData((fd) => ({ ...fd, photo: null })); }}
              />
            </div>
          </div>

          <div className="mt-4 border-t border-line pt-4">
            <label className="block text-sm font-medium text-theme-primary mb-2">
              {t('dc.notes')}
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={4}
              className="w-full p-3 bg-surface text-theme-primary border border-line rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
              placeholder={t('dc.notesPlaceholder')}
            />
          </div>

          <div className="flex gap-3 mt-4">
            <button
              onClick={handleSaveDoubleCheck}
              disabled={isSaving}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save size={18} />
              {tCommon('actions.save')}
            </button>
            <button
              onClick={resetForm}
              disabled={isSaving}
              className="flex items-center gap-2 px-4 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <X size={18} />
              {tCommon('actions.cancel')}
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {doubleChecks.map((doubleCheck) => (
          <div
            key={doubleCheck.id}
            className="bg-surface border border-line rounded-lg overflow-hidden"
          >
            <div className="p-4 flex items-center justify-between hover:bg-subtle">
              <div className="flex items-center gap-4 flex-1">
                <button
                  onClick={() => toggleDoubleCheck(doubleCheck.id)}
                  className="p-1 hover:bg-subtle rounded transition-colors"
                >
                  {expandedDoubleCheck === doubleCheck.id ? (
                    <ChevronUp size={20} className="text-theme-secondary" />
                  ) : (
                    <ChevronDown size={20} className="text-theme-secondary" />
                  )}
                </button>
                {doubleCheck.photo_url ? (
                  <img
                    src={doubleCheck.photo_url}
                    alt={doubleCheck.description}
                    className="w-16 h-16 object-cover rounded-lg border border-line"
                  />
                ) : (
                  <div className="w-16 h-16 bg-subtle rounded-lg flex items-center justify-center">
                    <Camera size={24} className="text-theme-secondary" />
                  </div>
                )}
                <div className="flex-1">
                  <h4 className="font-semibold text-theme-primary">{doubleCheck.description}</h4>
                  <p className="text-sm text-theme-secondary">{t('dc.sizeLabel')}: {doubleCheck.size}"</p>
                </div>
              </div>
              {!readOnly && (
                <div className="flex gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      startEditDoubleCheck(doubleCheck);
                    }}
                    className="p-2 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-500/10 rounded-lg transition-colors"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteConfirm({ id: doubleCheck.id, description: doubleCheck.description });
                    }}
                    className="p-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10 rounded-lg transition-colors"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              )}
            </div>

            {expandedDoubleCheck === doubleCheck.id && (
              <div className="border-t border-line bg-subtle p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h5 className="text-sm font-semibold text-theme-primary mb-1">{t('dc.descriptionLabel')}</h5>
                    <p className="text-theme-secondary">{doubleCheck.description}</p>
                  </div>
                  <div>
                    <h5 className="text-sm font-semibold text-theme-primary mb-1">{t('dc.sizeLabel')}</h5>
                    <p className="text-theme-secondary">{t('dc.inches', { n: doubleCheck.size })}</p>
                  </div>
                  {doubleCheck.gps_location && (
                    <div className="md:col-span-2">
                      <h5 className="text-sm font-semibold text-theme-primary mb-1">{t('gps.location')}</h5>
                      <div className="flex items-center gap-2">
                        <p className="text-theme-secondary">
                          {doubleCheck.gps_location.latitude.toFixed(6)}, {doubleCheck.gps_location.longitude.toFixed(6)}
                        </p>
                        <button
                          onClick={() => openMap(doubleCheck.gps_location!)}
                          className="flex items-center gap-1 px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 transition-colors"
                        >
                          <MapPin size={14} />
                          {t('gps.viewMap')}
                        </button>
                      </div>
                    </div>
                  )}
                  {doubleCheck.notes && (
                    <div className="md:col-span-2">
                      <h5 className="text-sm font-semibold text-theme-primary mb-1">{t('dc.notesLabel')}</h5>
                      <p className="text-theme-secondary whitespace-pre-wrap">{doubleCheck.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <ConfirmModal
        isOpen={deleteConfirm !== null}
        title={t('dc.deleteTitle')}
        message={deleteConfirm ? t('dc.deleteMessage', { desc: deleteConfirm.description }) : ''}
        onConfirm={async () => {
          if (deleteConfirm) {
            await handleDeleteDoubleCheck(deleteConfirm.id, deleteConfirm.description);
            setDeleteConfirm(null);
          }
        }}
        onCancel={() => setDeleteConfirm(null)}
      />

      {NotifyModal}
    </div>
  );
}
